import { ComponentType } from "react";
import { SvgIconProps } from "@material-ui/core";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import TruckOutlineIcon from "../../../common/icons/TruckOutlineIcon";
import TrailerOutlineIcon from "../../../common/icons/TrailerOutlineIcon";
import { DriverSelectionList } from "../../reports/safety/DriverSelectionList";
import { VehicleSelectionList } from "../../reports/vehicle-mileage-summary/VehicleSelectionList";

import { DataTransferCategoryKey, DataTransferMode } from "./dataTransferApi";

export type DataTransferChoiceSource =
  | "companies"
  | "groups"
  | "recordStatus"
  | "eldConnection";

export interface DataTransferFilterField {
  source: string;
  label: string;
  type: "select" | "multi-select" | "text";
  choicesSource?: DataTransferChoiceSource;
}

export interface DataTransferHistoryColumn {
  key:
    | "fileName"
    | "timeRequested"
    | "requestedBy"
    | "description"
    | "status"
    | "download"
    | "processed"
    | "period"
    | "driver"
    | "vehicle";
  label: string;
  sortField?: string;
  align?: "left" | "right" | "center";
}

export interface DataTransferCategoryConfig {
  key: DataTransferCategoryKey;
  label: string;
  description: string;
  entityLabel: string;
  entityPluralLabel: string;
  defaultExportDescription: string;
  defaultImportDescription: string;
  idField?: string;
  Icon: ComponentType<SvgIconProps>;
  SelectionComponent?: ComponentType<{ source: string; filter?: any }>;
  exportFilterFields: DataTransferFilterField[];
  historyColumns: Record<DataTransferMode, DataTransferHistoryColumn[]>;
  historySearchFields: Record<DataTransferMode, string[]>;
}

export const dataTransferCategories: DataTransferCategoryConfig[] = [
  {
    key: "drivers",
    label: "Drivers",
    description: "Import and export driver datasets.",
    entityLabel: "Driver",
    entityPluralLabel: "drivers",
    defaultExportDescription: "Driver export",
    defaultImportDescription: "Driver import",
    idField: "driverIds",
    Icon: AssignmentIndOutlinedIcon,
    SelectionComponent: DriverSelectionList,
    exportFilterFields: [
      {
        source: "status",
        label: "Driver status",
        type: "select",
        choicesSource: "recordStatus",
      },
      {
        source: "companyId",
        label: "Company",
        type: "select",
        choicesSource: "companies",
      },
    ],
    historyColumns: {
      export: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "status" },
        { key: "download", label: "Download", align: "right" },
      ],
      import: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "importStatus" },
        { key: "processed", label: "Processed", align: "right" },
      ],
    },
    historySearchFields: {
      export: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
      import: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
    },
  },
  {
    key: "vehicles",
    label: "Vehicles",
    description: "Import and export vehicle datasets.",
    entityLabel: "Vehicle",
    entityPluralLabel: "vehicles",
    defaultExportDescription: "Vehicle export",
    defaultImportDescription: "Vehicle import",
    idField: "vehicleIds",
    Icon: TruckOutlineIcon,
    SelectionComponent: VehicleSelectionList,
    exportFilterFields: [
      {
        source: "status",
        label: "Vehicle status",
        type: "select",
        choicesSource: "recordStatus",
      },
      {
        source: "companyId",
        label: "Company",
        type: "select",
        choicesSource: "companies",
      },
      {
        source: "groupId",
        label: "Group",
        type: "select",
        choicesSource: "groups",
      },
      {
        source: "eldStatus",
        label: "ELD status",
        type: "select",
        choicesSource: "eldConnection",
      },
    ],
    historyColumns: {
      export: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "status" },
        { key: "download", label: "Download", align: "right" },
      ],
      import: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "importStatus" },
        { key: "processed", label: "Processed", align: "right" },
      ],
    },
    historySearchFields: {
      export: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
      import: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
    },
  },
  {
    key: "trailers",
    label: "Trailers",
    description: "Import and export trailer datasets.",
    entityLabel: "Trailer",
    entityPluralLabel: "trailers",
    defaultExportDescription: "Trailer export",
    defaultImportDescription: "Trailer import",
    Icon: TrailerOutlineIcon,
    exportFilterFields: [
      {
        source: "searchText",
        label: "Search",
        type: "text",
      },
      {
        source: "status",
        label: "Trailer status",
        type: "select",
        choicesSource: "recordStatus",
      },
      {
        source: "companyId",
        label: "Company",
        type: "select",
        choicesSource: "companies",
      },
    ],
    historyColumns: {
      export: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "status" },
        { key: "download", label: "Download", align: "right" },
      ],
      import: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "importStatus" },
        { key: "processed", label: "Processed", align: "right" },
      ],
    },
    historySearchFields: {
      export: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
      import: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
    },
  },
  {
    key: "locations",
    label: "Locations & Events",
    description:
      "Import and export location packages together with related Geometris events.",
    entityLabel: "Location",
    entityPluralLabel: "locations and events",
    defaultExportDescription: "Locations and events export",
    defaultImportDescription: "Locations and events import",
    Icon: RoomOutlinedIcon,
    exportFilterFields: [],
    historyColumns: {
      export: [
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "period", label: "Period", sortField: "dateFrom" },
        { key: "driver", label: "Driver", sortField: "driverName" },
        { key: "vehicle", label: "Vehicle", sortField: "vehicleName" },
        { key: "status", label: "Status", sortField: "status" },
        { key: "download", label: "Download", align: "right" },
      ],
      import: [
        { key: "fileName", label: "File name", sortField: "fileName" },
        {
          key: "timeRequested",
          label: "Time requested",
          sortField: "creationTime",
        },
        {
          key: "requestedBy",
          label: "Requested by",
          sortField: "creatorName",
        },
        { key: "description", label: "Description", sortField: "description" },
        { key: "status", label: "Status", sortField: "importStatus" },
        { key: "processed", label: "Processed", align: "right" },
      ],
    },
    historySearchFields: {
      export: [
        "description",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
        "driverName",
        "vehicleName",
      ],
      import: [
        "description",
        "fileName",
        "creatorName",
        "creatorSurname",
        "creatorEmail",
      ],
    },
  },
];

export const getDataTransferCategory = (key?: string | null) =>
  dataTransferCategories.find((category) => category.key === key);
